import {
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faTrashCan,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";
import CustomButton from "../components/CustomButton";
import PageLayout from "../components/page-layout";
import axiosInstance from "../utils/axiosInstance";
import CreateUserModal from "../components/agentComponents/CreateAgentModel";
import { User } from "../providers/AuthProvider";
import { userHeading } from "../components/agentComponents/agentTableData";
import DeleteUserModal from "../components/agentComponents/DeleteAgentModel";
import PaginationMenu from "../components/pagination/pagination-menu";
import formQueryUrl from "../utils/formQueryUrl";
import UserRow, { UserCard } from "../components/agentComponents/AgentRow";
import NoItem from "../components/no-item";
import SkeltonTableRow from "../components/skelton/skelton-table-row";
import GetUser from "../utils/getUser";
import SectionHeader from "../components/section-header";
import { panelClass } from "../utils/ui-styles";

const Users = () => {
  const currentUser = GetUser();
  const [search, setSearch] = useDebounceValue("", 500);
  const [modal, setModal] = useState<User | boolean>(false);
  const [isDelete, setIsDelete] = useState<number | null>(null);
  const [users, setUsers] = useState<{ totalCount: number; data: User[] }>({
    totalCount: 0,
    data: [],
  });
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);

  const fetchUsers = useCallback(
    async (options?: { silent?: boolean }) => {
      if (currentUser?.type === "agent") return;

      if (!options?.silent) {
        setLoading(true);
      }

      try {
        const filters = [search && { search }];
        const res = await axiosInstance.get(
          `/user?page=${page}&limit=${pageSize}${formQueryUrl(filters)}`,
        );
        setUsers(res.data);
        hasLoadedOnce.current = true;
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [search, page, pageSize, currentUser?.type],
  );

  useEffect(() => {
    fetchUsers({ silent: hasLoadedOnce.current });
  }, [fetchUsers]);

  if (currentUser?.type === "agent") {
    return <Navigate to="/" replace />;
  }

  const openInviteModal = () => {
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
  };

  const openEditModal = (user: User) => {
    setModal(user);
  };

  const toggleDeleteModal = (id?: number) => {
    setIsDelete(id ?? null);
  };

  const handleUsersChanged = () => {
    fetchUsers({ silent: true });
  };

  const tableAction = [
    {
      name: "Edit User",
      icon: faPenToSquare,
      actionHandler: openEditModal,
    },
    {
      name: "Delete User",
      icon: faTrashCan,
      actionHandler: (data: User) => toggleDeleteModal(data?.id),
    },
  ];

  const showSkeleton = loading && users.data.length === 0;

  return (
    <PageLayout
      pageTitle="Users"
      subtitle="Invite users and manage access to HealthSure"
      primaryAction={
        <CustomButton
          className="!w-full !rounded-xl !font-[Gilroy-Medium] !text-[14px] sm:!w-auto"
          variant="primary"
          renderLeftIcon={
            <FontAwesomeIcon icon={faPlus} className="text-[14px]" />
          }
          onClick={openInviteModal}
        >
          Invite user
        </CustomButton>
      }
    >
      {isDelete && (
        <DeleteUserModal
          ToggleDeleteModel={() => toggleDeleteModal()}
          id={isDelete}
          onSuccess={handleUsersChanged}
        />
      )}
      {modal && (
        <CreateUserModal
          ToggleModel={closeModal}
          isEdit={modal}
          onSuccess={handleUsersChanged}
        />
      )}

      <div className={`${panelClass} min-w-0`}>
        <SectionHeader
          title="Team"
          icon={faUsers}
          subtitle={`${users.totalCount} ${users.totalCount === 1 ? "user" : "users"}`}
          actions={
            <label className="flex h-11 w-full min-w-0 items-center gap-3 rounded-xl border border-edge bg-input-bg px-4 transition-colors hover:border-edge-strong focus-within:border-accent sm:w-80">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="text-[13px] text-label-muted" />
              <span className="sr-only">Search users</span>
              <input
                type="search"
                placeholder="Search name, email, phone or code"
                className="min-w-0 flex-1 bg-transparent font-[Gilroy-Medium] text-[14px] text-heading-color placeholder:text-placeholder focus:outline-none"
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          }
        />

        <div className="space-y-3 lg:hidden">
          {showSkeleton
            ? Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse rounded-xl border border-edge bg-surface p-4"
                >
                  <div className="h-4 w-1/2 rounded bg-skeleton" />
                  <div className="mt-2 h-3 w-3/4 rounded bg-skeleton" />
                  <div className="mt-4 flex gap-2">
                    <div className="h-6 w-16 rounded-full bg-skeleton" />
                    <div className="h-6 w-20 rounded-full bg-skeleton" />
                  </div>
                </div>
              ))
            : users.data.map((row) => (
                <UserCard
                  key={row.id}
                  row={row}
                  tableAction={tableAction}
                />
              ))}
        </div>

        <div className="hidden overflow-x-auto rounded-xl border border-edge lg:block">
          <table className="h-max w-full min-w-[720px] table-auto text-left">
            <thead>
              <tr className="border-b border-edge bg-surface-muted font-[Gilroy-Medium]">
                {userHeading.map((data, index) => (
                  <th
                    key={index}
                    scope="col"
                    className="px-4 py-3 text-[12px] font-normal text-label-muted"
                  >
                    {data.header}
                  </th>
                ))}
                <th scope="col" className="px-4 py-3 text-end text-[12px] font-normal text-label-muted">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface">
              {showSkeleton ? (
                <SkeltonTableRow />
              ) : (
                users.data.map((row) => (
                  <UserRow
                    key={row.id}
                    row={row}
                    tableAction={tableAction}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {!showSkeleton && !users.totalCount && (
          <NoItem
            title="No users found"
            desc="Invite a user to get started"
          />
        )}

        <PaginationMenu
          totalCount={users.totalCount}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </div>
    </PageLayout>
  );
};

export default Users;
